/** React Query cache key for {@link EmployeeShiftApiSettings}. */
export const SHIFT_API_SETTINGS_QUERY_KEY = ["shifts", "apiSettings"] as const;

/**
 * Top-level `settings` on employee shift JWT success responses (see API docs).
 * Only fields used by the app are required; the rest are optional for forward compatibility.
 */
export interface EmployeeShiftApiSettings {
  geofencing_enabled: boolean;
  allow_shift_start_outside_geofence?: boolean;
  allow_shift_end_outside_geofence?: boolean;
  max_shift_length_hours?: number;
  allow_manual_time_entry?: boolean;
  require_manager_approval_for_manual?: boolean;
  require_reason_for_edit?: boolean;
  auto_end_shift_after_hours?: number;
  allow_open_shifts?: boolean;
  auto_flag_missing_end_after_hours?: number;
  block_end_if_tasks_incomplete?: boolean;
  shift_alerts_enabled?: boolean;
  grace_period_minutes?: number;
  only_scheduled_shifts_enabled?: boolean;
  only_scheduled_shifts_allowed_deviation_minutes?: number;
  no_show_enabled?: boolean;
  no_show_threshold_minutes?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Reads `settings` from a success JSON body (`{ data?, settings?, success? }`).
 */
export function parseEmployeeShiftApiSettings(
  response: unknown
): EmployeeShiftApiSettings | undefined {
  if (!isRecord(response)) return undefined;
  const raw = response.settings;
  if (!isRecord(raw)) return undefined;

  if (typeof raw.geofencing_enabled !== "boolean") return undefined;

  const out: EmployeeShiftApiSettings = {
    geofencing_enabled: raw.geofencing_enabled,
  };

  if (typeof raw.allow_shift_start_outside_geofence === "boolean") {
    out.allow_shift_start_outside_geofence = raw.allow_shift_start_outside_geofence;
  }
  if (typeof raw.allow_shift_end_outside_geofence === "boolean") {
    out.allow_shift_end_outside_geofence = raw.allow_shift_end_outside_geofence;
  }
  if (typeof raw.max_shift_length_hours === "number") {
    out.max_shift_length_hours = raw.max_shift_length_hours;
  }
  if (typeof raw.allow_manual_time_entry === "boolean") {
    out.allow_manual_time_entry = raw.allow_manual_time_entry;
  }
  if (typeof raw.require_manager_approval_for_manual === "boolean") {
    out.require_manager_approval_for_manual = raw.require_manager_approval_for_manual;
  }
  if (typeof raw.require_reason_for_edit === "boolean") {
    out.require_reason_for_edit = raw.require_reason_for_edit;
  }
  if (typeof raw.auto_end_shift_after_hours === "number") {
    out.auto_end_shift_after_hours = raw.auto_end_shift_after_hours;
  }
  if (typeof raw.allow_open_shifts === "boolean") {
    out.allow_open_shifts = raw.allow_open_shifts;
  }
  if (typeof raw.auto_flag_missing_end_after_hours === "number") {
    out.auto_flag_missing_end_after_hours = raw.auto_flag_missing_end_after_hours;
  }
  if (typeof raw.block_end_if_tasks_incomplete === "boolean") {
    out.block_end_if_tasks_incomplete = raw.block_end_if_tasks_incomplete;
  }
  if (typeof raw.shift_alerts_enabled === "boolean") {
    out.shift_alerts_enabled = raw.shift_alerts_enabled;
  }
  if (typeof raw.grace_period_minutes === "number") {
    out.grace_period_minutes = raw.grace_period_minutes;
  }
  if (typeof raw.only_scheduled_shifts_enabled === "boolean") {
    out.only_scheduled_shifts_enabled = raw.only_scheduled_shifts_enabled;
  }
  if (typeof raw.only_scheduled_shifts_allowed_deviation_minutes === "number") {
    out.only_scheduled_shifts_allowed_deviation_minutes =
      raw.only_scheduled_shifts_allowed_deviation_minutes;
  }
  if (typeof raw.no_show_enabled === "boolean") {
    out.no_show_enabled = raw.no_show_enabled;
  }
  if (typeof raw.no_show_threshold_minutes === "number") {
    out.no_show_threshold_minutes = raw.no_show_threshold_minutes;
  }

  return out;
}
